/**
 * Report Viewer - Display report with citations, evidence tracing, and collaboration
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { RichTextEditor } from './RichTextEditor';
import { getReport, downloadReportV1, downloadReportV2, downloadReportPDF, downloadReportDOCX, downloadReportXLSX, trackReportView, trackReportDownload, removeCitation, regenerateSections } from '../../api/reports';
import { ReportAnalytics } from './ReportAnalytics';
import { ReportSharing } from './ReportSharing';
import { ReportCollaboration } from './ReportCollaboration';
import { ReportActions } from './ReportActions';
import { AddCitationModal } from './AddCitationModal';
import { CustomSectionsTab } from './CustomSectionsTab';
import { QAChecklistPanel, QAFormattingPanel, QAGrammarPanel, QAPreSubmissionPanel } from '../QA';
import { usePermissions } from '../../hooks/usePermissions';
import { useFeatureFlag } from '../../contexts/ConfigContext';
import { ReviewDashboard } from '../Review';
import { StaffAssignmentsTab } from './StaffAssignmentsTab';
import {
  FileText,
  Download,
  Edit,
  Share2,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  BookOpen,
  ExternalLink,
  GitBranch,
  History,
  TrendingUp,
  Shield,
  Code,
  FileCode,
  Save,
  X as XIcon,
  Trash2,
  RefreshCw,
  HelpCircle,
  Plus,
  Scissors,
  ClipboardCheck,
  Users,
} from 'lucide-react';

interface ReportViewerProps {
  reportId: string;
  onEdit?: () => void;
  onClose?: () => void;
}

interface ReportVersion {
  version: number;
  created_at: string;
  created_by: string;
  changes: string;
  status: string;
}

// QA Tab Component (available for all reports)
function QATabContent({ reportId }: { reportId: string }) {
  const { hasPermission } = usePermissions();
  const isReviewer = hasPermission('review') || hasPermission('keystone_rfp_review');
  const checklistType = isReviewer ? 'reviewer' : 'writer';
  
  return (
    <div className="space-y-6">
      <QAChecklistPanel
        reportId={reportId}
        checklistType={checklistType}
      />
      <QAFormattingPanel reportId={reportId} />
      <QAGrammarPanel reportId={reportId} />
      <QAPreSubmissionPanel reportId={reportId} />
    </div>
  );
}

// Word Count Tab Component (dynamically loaded - available for all reports)
function WordCountTabContent({ reportId, report, enableOptimizer = true }: { reportId: string; report: any; enableOptimizer?: boolean }) {
  const [WordCountTab, setWordCountTab] = React.useState<React.ComponentType<any> | null>(null);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    // Try to import from rfp-agent plugin
    // This will work if the plugin is loaded
    const loadWordCountTab = async () => {
      try {
        // Try relative path from core to plugins
        // From: tracekit/packages/core/src/components/Reports/ReportViewer.tsx
        // To: tracekit/packages/plugins/rfp-agent/src/components/WordCountTab.tsx
        // Try import using the alias defined in vite config
        // This works when running in keystone-rfp app which has the alias configured
        try {
          // Dynamic import from plugin, only available at runtime when plugin is loaded
          // Use relative path that Rollup can resolve during build
          // Path: core/src/components/Reports -> packages/plugins/rfp-agent/src/index
          // @ts-ignore - Plugin path may not be available at compile time
          const module = await import('../../../../plugins/rfp-agent/src/index');
          if (module && module.WordCountTab) {
            // Wrap the component to pass enableOptimizer prop
            setWordCountTab(() => (props: any) => <module.WordCountTab {...props} enableOptimizer={enableOptimizer} />);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.debug('WordCountTab import failed (relative path from index):', e);
        }
        
        try {
          // Fallback: try direct component import with relative path
          // @ts-ignore - Plugin path may not be available at compile time
          const module = await import('../../../../plugins/rfp-agent/src/components/WordCountTab');
          if (module && module.WordCountTab) {
            // Wrap the component to pass enableOptimizer prop
            setWordCountTab(() => (props: any) => <module.WordCountTab {...props} enableOptimizer={enableOptimizer} />);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.debug('WordCountTab import failed (relative path to component):', e);
        }
        
        try {
          // Final fallback: try alias path (only works at runtime when alias is configured)
          // Construct path dynamically to prevent Rollup from analyzing it
          // @ts-ignore - Dynamic import path
          const pluginPath = '@tracekit' + '/plugin-rfp-agent';
          const module = await import(/* @vite-ignore */ pluginPath);
          if (module && module.WordCountTab) {
            // Wrap the component to pass enableOptimizer prop
            setWordCountTab(() => (props: any) => <module.WordCountTab {...props} enableOptimizer={enableOptimizer} />);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.debug('WordCountTab import failed (alias path):', e);
        }
        
        // If import fails, show fallback UI
        setLoading(false);
      } catch (error) {
        console.warn('WordCountTab not available:', error);
        setLoading(false);
      }
    };
    
    loadWordCountTab();
  }, [enableOptimizer]);
  
  if (loading) {
    return (
      <Card>
        <div className="p-4 text-center text-gray-500">Loading word count tools...</div>
      </Card>
    );
  }
  
  if (!WordCountTab) {
    return (
      <Card>
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-2">Word Count Optimization</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Word count optimization features are available for all reports. 
            Please ensure the RFP Agent plugin is properly loaded.
          </p>
        </div>
      </Card>
    );
  }
  
  return <WordCountTab reportId={reportId} report={report} enableOptimizer={enableOptimizer} />;
}

// Resume Job Button Component
function ResumeJobButton({ reportId, report, queryClient }: { reportId: string; report: any; queryClient: any }) {
  const [jobStatus, setJobStatus] = useState<any>(null);
  const [isResuming, setIsResuming] = useState(false);
  
  useEffect(() => {
    // Check if job is stuck (questions incomplete)
    const totalQuestions = report?.stats?.total_questions || report?.total_questions || 0;
    const answeredQuestions = report?.stats?.answered || report?.answered_questions || 0;
    const isStuck = totalQuestions > 0 && answeredQuestions < totalQuestions && report?.status === 'generating';
    
    if (isStuck) {
      // Try to fetch job status
      import('../../api/reports').then(({ getReportJob }) => {
        getReportJob(reportId).then(setJobStatus).catch(() => {});
      });
    }
  }, [reportId, report]);
  
  const handleResumeJob = async () => {
    if (!confirm('Resume the stuck report generation job? This will continue processing remaining questions.')) {
      return;
    }
    
    setIsResuming(true);
    try {
      const { resumeReportJob } = await import('../../api/reports');
      await resumeReportJob(reportId);
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      alert('Job resumed successfully. It will continue processing remaining questions.');
    } catch (error: any) {
      console.error('Failed to resume job:', error);
      alert(`Failed to resume job: ${error.message || 'Unknown error'}`);
    } finally {
      setIsResuming(false);
    }
  };
  
  const totalQuestions = report?.stats?.total_questions || report?.total_questions || 0;
  const answeredQuestions = report?.stats?.answered || report?.answered_questions || 0;
  const isStuck = totalQuestions > 0 && answeredQuestions < totalQuestions && 
                 (report?.status === 'generating' || jobStatus?.status === 'running' || jobStatus?.status === 'checkpointed');
  
  if (!isStuck) {
    return null;
  }
  
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleResumeJob}
      disabled={isResuming}
    >
      {isResuming ? 'Resuming...' : `Resume Job (${answeredQuestions}/${totalQuestions})`}
    </Button>
  );
}

export function ReportViewer({ reportId, onEdit, onClose }: ReportViewerProps) {
  const enableCustomSections = useFeatureFlag('enableCustomSections');
  const enableWordCountOptimizer = useFeatureFlag('enableWordCountOptimizer');
  const enableQualityAssurance = useFeatureFlag('enableQualityAssurance');
  const enableReviewWorkflow = useFeatureFlag('enableReviewWorkflow');
  const [activeTab, setActiveTab] = useState<'content' | 'citations' | 'versions' | 'collaboration' | 'sharing' | 'analytics' | 'actions' | 'wordcount' | 'customsections' | 'qa' | 'staffassignments' | 'review'>('content');
  const [reportContent, setReportContent] = useState<string>('');
  const [reportV1Content, setReportV1Content] = useState<string>('');
  const [selectedCitation, setSelectedCitation] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'markdown' | 'rendered'>('rendered');
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState<string>('');
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [removedArtifactIds, setRemovedArtifactIds] = useState<Set<string>>(new Set());
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState<string>('');
  const [showAddCitationModal, setShowAddCitationModal] = useState(false);
  const [addCitationSectionId, setAddCitationSectionId] = useState<string | undefined>();
  const [addCitationSectionName, setAddCitationSectionName] = useState<string | undefined>();
  const [selectedCitationIds, setSelectedCitationIds] = useState<Set<string>>(new Set()); // For bulk operations
  const [isBulkMode, setIsBulkMode] = useState(false);
  const queryClient = useQueryClient();

  // Fetch report metadata
  const { data: report, isLoading } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => getReport(reportId),
    enabled: !!reportId,
  });
  
  // Initialize selectedVersion based on current version
  React.useEffect(() => {
    if (report && !selectedVersion) {
      const currentVersion = report.meta?.current_version || 2;
      setSelectedVersion(`v${currentVersion}`);
    }
  }, [report, selectedVersion]);
  
  // Initialize editedName when report loads or name changes
  React.useEffect(() => {
    if (report && !isEditingName) {
      const reportName = report.name || report.meta?.name || report.client_name;
      setEditedName(reportName);
    }
  }, [report?.name, report?.meta?.name, report?.client_name, isEditingName]);

  // Track view
  React.useEffect(() => {
    if (reportId) {
      trackReportView(reportId).catch(err => console.error('Failed to track view:', err));
    }
  }, [reportId]);

  // Load report content based on selected version
  React.useEffect(() => {
    if (reportId) {
      const versionToLoad = selectedVersion || (report?.meta?.current_version ? `v${report.meta.current_version}` : 'v2');
      
      // Load selected version for main content display
      const loadVersion = async () => {
        try {
          if (versionToLoad === 'v1') {
            const content = await downloadReportV1(reportId);
            setReportContent(content);
            setEditedContent(content);
          } else if (versionToLoad === 'v2') {
            const content = await downloadReportV2(reportId);
            setReportContent(content);
            setEditedContent(content);
          } else {
            // Higher versions (v3, v4, etc.)
            const { getAPIClient } = await import('../../api/client');
            const client = getAPIClient();
            const content = await client.get<string>(`/reports/${reportId}/${versionToLoad}.md`, {
              responseType: 'text',
              params: { view: 'true' }, // Bypass status check for viewing
            } as any);
            setReportContent(content);
            setEditedContent(content);
          }
        } catch (err) {
          console.error(`Failed to load ${versionToLoad} report content:`, err);
        }
      };
      
      loadVersion();
      
      // Always load v1 for citation extraction
      downloadReportV1(reportId)
        .then(content => {
          setReportV1Content(content);
        })
        .catch(err => console.error('Failed to load v1 report content:', err));
    }
  }, [reportId, selectedVersion, report?.meta?.current_version]);

  // Convert markdown to HTML (proper implementation)
  const convertMarkdownToHTML = (markdown: string): string => {
    if (!markdown) return '';
    
    let html = markdown;

    // Escape HTML first
    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Code blocks (must be done before inline code)
    html = html.replace(/```([^`]+)```/g, '<pre class="bg-gray-100 p-4 rounded-lg overflow-x-auto my-4 border border-gray-300"><code class="text-sm font-mono">$1</code></pre>');

    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-2 py-0.5 rounded text-sm font-mono text-gray-800">$1</code>');

    // Headers (must be on new lines) - reduced spacing
    html = html.replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold text-gray-800 mt-4 mb-2">$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold text-gray-900 dark:text-gray-100 mt-5 mb-3 pb-2 border-b-2 border-gray-200 dark:border-gray-700">$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-6 mb-4 pb-3 border-b-3 border-blue-500 dark:border-blue-400">$1</h1>');

    // Bold (before italic to handle **)
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-gray-100">$1</strong>');
    
    // Italic
    html = html.replace(/\*([^*]+)\*/g, '<em class="italic text-gray-700">$1</em>');

    // Links (including citations like [1])
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:text-blue-800 underline font-medium">$1</a>');

    // Unordered lists - FIXED: Reset list state properly to prevent staircase indentation
    const lines = html.split('\n');
    let inList = false;
    let listType: 'ul' | 'ol' | null = null;
    let processedLines: string[] = [];
    let listIndentLevel = 0; // Track nesting level
    
    lines.forEach(line => {
      const trimmed = line.trim();
      const leadingSpaces = line.length - line.trimStart().length;
      const currentIndent = Math.floor(leadingSpaces / 2); // Approximate indent level
      
      // Check for list items
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        // Close previous list if different type or different indent
        if (inList && (listType !== 'ul' || currentIndent !== listIndentLevel)) {
          processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
          inList = false;
          listType = null;
          listIndentLevel = 0;
        }
        
        if (!inList) {
          processedLines.push('<ul class="list-disc list-inside ml-4 my-3 space-y-1">');
          inList = true;
          listType = 'ul';
          listIndentLevel = currentIndent;
        }
        processedLines.push(`<li class="text-gray-700 dark:text-gray-300">${trimmed.substring(2)}</li>`);
      } else if (trimmed.match(/^\d+\.\s/)) {
        // Close previous list if different type or different indent
        if (inList && (listType !== 'ol' || currentIndent !== listIndentLevel)) {
          processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
          inList = false;
          listType = null;
          listIndentLevel = 0;
        }
        
        if (!inList) {
          processedLines.push('<ol class="list-decimal list-inside ml-4 my-3 space-y-1">');
          inList = true;
          listType = 'ol';
          listIndentLevel = currentIndent;
        }
        processedLines.push(`<li class="text-gray-700 dark:text-gray-300">${trimmed.replace(/^\d+\.\s/, '')}</li>`);
      } else {
        // Close list if we're leaving list context
        if (inList) {
          processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
          inList = false;
          listType = null;
          listIndentLevel = 0;
        }
        
        if (trimmed === '') {
          // Skip empty lines to reduce spacing (don't add <br/>)
          // This prevents excessive spacing in rendered view
        } else if (!trimmed.startsWith('<')) {
          // Regular paragraph - reduced spacing
          processedLines.push(`<p class="text-gray-800 dark:text-gray-200 mb-3 leading-relaxed">${line}</p>`);
        } else {
          processedLines.push(line);
        }
      }
    });
    
    // Close any remaining list
    if (inList) {
      processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
    }

    return processedLines.join('\n');
  };

  const handleSaveContent = async () => {
    try {
      // Call API to save edited content as new version
      const { saveReportContent } = await import('../../api/reports');
      const result = await saveReportContent(reportId, editedContent, 'v2');
      
      setReportContent(editedContent);
      setIsEditing(false);
      
      // Refresh report to get updated version info
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      
      alert(`Report content saved as ${result.version} successfully`);
    } catch (error: any) {
      console.error('Failed to save report content:', error);
      alert(`Failed to save: ${error.message || 'Unknown error'}`);
    }
  };

  // Mock versions data (will be replaced with real API)
  // Build versions array from report.versions and meta (includes v1, v2, and all higher versions)
  const versions: ReportVersion[] = (() => {
    const versionList: ReportVersion[] = [];
    
    // Get current version number
    const currentVersionNum = report?.meta?.current_version || 2;
    
    // Add v1 and v2 - they always exist for AI-generated reports
    if (report) {
      // v1 always exists for AI reports
      versionList.push({
        version: 1,
        status: currentVersionNum === 1 ? 'current' : 'archived',
        changes: 'V1 - Research Draft',
        created_by: 'System (Auto-generated)',
        created_at: report.created_at || new Date().toISOString()
      });
      // v2 always exists for AI reports
      versionList.push({
        version: 2,
        status: currentVersionNum === 2 ? 'current' : 'archived',
        changes: 'V2 - Executive Summary',
        created_by: 'System (Auto-generated)',
        created_at: report.created_at || new Date().toISOString()
      });
    }
    
    // Add higher versions (v3, v4, etc.) from meta
    if (report?.meta && typeof report.meta === 'object') {
      const meta = report.meta as Record<string, any>;
      
      // Find all version numbers from blob_path keys
      const foundVersions = new Set<number>();
      Object.keys(meta).forEach(key => {
        const match = key.match(/^v(\d+)_blob_path$/);
        if (match) {
          const versionNum = parseInt(match[1]);
          if (versionNum >= 3) {
            foundVersions.add(versionNum);
          }
        }
      });
      
      // Add found versions
      Array.from(foundVersions).forEach(v => {
        const changeSummary = meta[`v${v}_change_summary`] || 'Manual edit';
        const createdBy = meta[`v${v}_created_by`] || 'Unknown';
        const createdAt = meta[`v${v}_created_at`] || report.updated_at || report.created_at || new Date().toISOString();
        
        versionList.push({
          version: v,
          status: v === currentVersionNum ? 'current' : 'archived',
          changes: changeSummary,
          created_by: createdBy,
          created_at: createdAt
        });
      });
    }
    
    // Also add versions up to current_version if not already found
    for (let v = 3; v <= currentVersionNum; v++) {
      if (!versionList.find(vv => vv.version === v)) {
        versionList.push({
          version: v,
          status: v === currentVersionNum ? 'current' : 'archived',
          changes: 'Manual edit',
          created_by: 'Unknown',
          created_at: report?.updated_at || report?.created_at || new Date().toISOString()
        });
      }
    }
    
    // Sort by version number descending (newest first)
    versionList.sort((a, b) => b.version - a.version);
    
    return versionList;
  })();

  const handleDownload = async (format: 'v1' | 'v2' | 'pdf' | 'docx' | 'xlsx') => {
    try {
      // Export guardrails: Check if document status allows export
      const allowedStatuses = ['approved', 'archived', 'submitted'];
      const currentStatus = report?.status || 'draft';
      
      // PDF, DOCX, XLSX, and V2 exports require approved/archived/submitted status
      if ((format === 'pdf' || format === 'docx' || format === 'xlsx' || format === 'v2') && !allowedStatuses.includes(currentStatus)) {
        alert('Document must be approved, archived, or submitted before export. Current status: ' + currentStatus);
        return;
      }
      
      // Track the download
      await trackReportDownload(reportId, format);
      
      if (format === 'pdf') {
        const blob = await downloadReportPDF(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = (report?.name || report?.client_name || 'report')?.replace(/[^a-z0-9]/gi, '_');
        a.download = `${fileName}_${reportId.substring(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else if (format === 'docx') {
        const blob = await downloadReportDOCX(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = (report?.name || report?.client_name || 'report')?.replace(/[^a-z0-9]/gi, '_');
        a.download = `${fileName}_${reportId.substring(0, 8)}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else if (format === 'xlsx') {
        const blob = await downloadReportXLSX(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = (report?.name || report?.client_name || 'report')?.replace(/[^a-z0-9]/gi, '_');
        a.download = `${fileName}_${reportId.substring(0, 8)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        const content = format === 'v1'
          ? await downloadReportV1(reportId)
          : await downloadReportV2(reportId);
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = (report?.name || report?.client_name || 'report')?.replace(/[^a-z0-9]/gi, '_');
        a.download = `${fileName}_${format}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      
      // Refresh analytics
      queryClient.invalidateQueries({ queryKey: ['report-analytics', reportId] });
    } catch (error) {
      console.error('Download failed:', error);
      alert(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleTraceEvidence = async (evidenceId: string) => {
    try {
      // Call evidence trace API
      const { traceEvidence } = await import('../../api/evidence');
      const traceData = await traceEvidence(evidenceId);
      
      // Open modal with trace data (includes chunk_id and full content)
      setSelectedCitation({
        evidence_id: evidenceId,
        trace: traceData,
        chunk_id: traceData.chunk?.id,
        chunk_content: traceData.chunk?.content,
      });
    } catch (error) {
      console.error('Failed to trace evidence:', error);
      alert('Failed to trace evidence source');
    }
  };

  const handleDownloadArtifact = async (artifactId: string, filename: string) => {
    try {
      const client = (await import('../../api/client')).getAPIClient();
      const blob = await client.get(`/artifacts/${artifactId}/download`, {
        responseType: 'blob',
      });
      
      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download artifact:', error);
      alert('Failed to download source document');
    }
  };

  const handleDownloadByFilename = async (filename: string, artifactId?: string) => {
    try {
      const client = (await import('../../api/client')).getAPIClient();
      
      // If we have artifact_id, use it directly
      if (artifactId) {
        await handleDownloadArtifact(artifactId, filename);
        return;
      }
      
      // Otherwise, search for the artifact by filename
      const { listArtifacts } = await import('../../api/artifacts');
      const artifactsResponse = await listArtifacts(report?.project_id || '');
      
      // Find artifact matching this filename
      const artifact = artifactsResponse.artifacts.find((a: any) => a.filename === filename);
      
      if (artifact) {
        await handleDownloadArtifact(artifact.id, filename);
      } else {
        alert(`Could not find artifact: ${filename}\n\nThe document may have been deleted or you may not have access to it.`);
      }
    } catch (error) {
      console.error('Failed to download by filename:', error);
      alert(`Failed to download: ${filename}`);
    }
  };

  const handleRemoveCitation = async (citation: any) => {
    if (!citation.artifact_id) {
      alert('Cannot remove citation: artifact ID not available');
      return;
    }
    
    // Find which sections use this citation
    const affectedSections = citation.sections || [citation.section || 'Unknown'];
    const sectionIds = report?.sections
      ?.filter((s: any) => affectedSections.includes(s.section_name))
      .map((s: any) => s.id) || [];
    
    const confirmMessage = `Remove this citation from the report?\n\n` +
      `Source: ${citation.source}\n` +
      `Affected Sections: ${affectedSections.join(', ')}\n` +
      `This will remove the citation and you can regenerate ${sectionIds.length} section(s) without this source.`;
    
    if (!confirm(confirmMessage)) {
      return;
    }
    
    try {
      await removeCitation(reportId, { 
        artifact_id: citation.artifact_id, 
        section_id: sectionIds.length === 1 ? sectionIds[0] : undefined 
      });
      
      // Track removed artifact and affected sections
      setRemovedArtifactIds(prev => new Set([...prev, citation.artifact_id]));
      
      // Refresh report content
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      
      // Reload report content
      downloadReportV1(reportId)
        .then(content => setReportV1Content(content))
        .catch(err => console.error('Failed to reload v1:', err));
      downloadReportV2(reportId)
        .then(content => {
          setReportContent(content);
          setEditedContent(content);
        })
        .catch(err => console.error('Failed to reload v2:', err));
      
      alert(`Citation removed successfully. ${sectionIds.length} section(s) affected. Use "Regenerate Sections" to update the report content without this source.`);
    } catch (error: any) {
      console.error('Failed to remove citation:', error);
      alert(`Failed to remove citation: ${error.message || 'Unknown error'}`);
    }
  };

  const handleRegenerateSections = async (specificSectionIds?: string[]) => {
    if (removedArtifactIds.size === 0) {
      alert('No sources have been removed. Remove citations first before regenerating.');
      return;
    }
    
    if (!report?.sections || report.sections.length === 0) {
      alert('No sections found to regenerate.');
      return;
    }
    
    const excludedList = Array.from(removedArtifactIds);
    // Use specific section IDs if provided, otherwise regenerate all sections
    const sectionIds = specificSectionIds || report.sections.map((s: any) => s.id);
    
    // Get section names for confirmation
    const sectionNames = report.sections
      .filter((s: any) => sectionIds.includes(s.id))
      .map((s: any) => s.section_name)
      .join(', ');
    
    if (!confirm(
      `Regenerate ${sectionIds.length} section(s) excluding ${excludedList.length} source(s)?\n\n` +
      `Sections to regenerate:\n${sectionNames}\n\n` +
      `Excluded sources:\n${excludedList.slice(0, 5).map(id => id.substring(0, 8) + '...').join(', ')}${excludedList.length > 5 ? ` and ${excludedList.length - 5} more...` : ''}\n\n` +
      `This will regenerate the selected sections without using the excluded sources.`
    )) {
      return;
    }
    
    setIsRegenerating(true);
    try {
      const response = await regenerateSections(reportId, {
        section_ids: sectionIds,
        excluded_artifact_ids: excludedList
      });
      
      // Clear removed artifacts (they've been processed)
      setRemovedArtifactIds(new Set());
      
      // Refresh report
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      
      // Reload report content
      downloadReportV1(reportId)
        .then(content => setReportV1Content(content))
        .catch(err => console.error('Failed to reload v1:', err));
      downloadReportV2(reportId)
        .then(content => {
          setReportContent(content);
          setEditedContent(content);
        })
        .catch(err => console.error('Failed to reload v2:', err));
      
      alert(`Successfully regenerated ${response.sections_regenerated} section(s) without the excluded sources.\n\nNew stats:\n- Total Citations: ${response.new_stats.total_citations}\n- Avg Confidence: ${(response.new_stats.avg_confidence * 100).toFixed(1)}%`);
    } catch (error: any) {
      console.error('Failed to regenerate sections:', error);
      alert(`Failed to regenerate sections: ${error.message || 'Unknown error'}`);
    } finally {
      setIsRegenerating(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card>
        <p className="text-center py-12 text-gray-500">Report not found</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-6 w-6 text-blue-600" />
              <div className="flex items-center gap-2">
                {(() => {
                  const reportName = report.name || report.meta?.name || report.client_name;
                  
                  const handleSaveName = async () => {
                    try {
                      const { updateReport } = await import('../../api/reports');
                      await updateReport(reportId, { name: editedName });
                      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
                      setIsEditingName(false);
                    } catch (error: any) {
                      console.error('Failed to update name:', error);
                      alert(`Failed to update name: ${error.message || 'Unknown error'}`);
                      setEditedName(reportName);
                    }
                  };
                  
                  if (isEditingName) {
                    return (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          className="px-2 py-1 border border-gray-300 rounded text-2xl font-bold text-gray-900 dark:text-gray-100"
                          onBlur={handleSaveName}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveName();
                            } else if (e.key === 'Escape') {
                              setIsEditingName(false);
                              setEditedName(reportName);
                            }
                          }}
                          autoFocus
                        />
                      </div>
                    );
                  }
                  
                  return (
                    <h1 
                      className="text-2xl font-bold text-gray-900 dark:text-gray-100 cursor-pointer hover:underline"
                      onClick={() => setIsEditingName(true)}
                      title="Click to edit name"
                    >
                      {reportName}
                    </h1>
                  );
                })()}
              </div>
            </div>
            <p className="text-gray-600 capitalize">{report.report_type?.replace(/_/g, ' ')}</p>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Created: {new Date(report.created_at).toLocaleDateString()}
              </span>
              {report.updated_at && (
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Updated: {new Date(report.updated_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2 items-center">
            {(() => {
              const allowedStatuses = ['approved', 'archived', 'submitted'];
              const currentStatus = report?.status || 'draft';
              const canExport = allowedStatuses.includes(currentStatus);
              const exportTooltip = canExport 
                ? '' 
                : `Proposals can only be exported once a reviewer marks them as approved, archived, or submitted. Current status: ${currentStatus}`;
              
              return (
                <>
                  <div className="flex items-center gap-1">
                    <Button 
                      onClick={() => handleDownload('pdf')}
                      disabled={!canExport}
                      title={exportTooltip}
                    >
                      <Download className="h-4 w-4" />
                      PDF
                    </Button>
                    {!canExport && (
                      <div className="group relative">
                        <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          {exportTooltip}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="outline" 
                      onClick={() => handleDownload('docx')}
                      disabled={!canExport}
                      title={exportTooltip}
                    >
                      <Download className="h-4 w-4" />
                      DOCX
                    </Button>
                    {!canExport && (
                      <div className="group relative">
                        <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          {exportTooltip}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="outline" 
                      onClick={() => handleDownload('xlsx')}
                      disabled={!canExport}
                      title={exportTooltip}
                    >
                      <Download className="h-4 w-4" />
                      XLSX
                    </Button>
                    {!canExport && (
                      <div className="group relative">
                        <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          {exportTooltip}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
            {/* Edit button removed - use "Edit Content" button in content tab instead */}
          </div>
        </div>

        {/* Status Card */}
        <div className="mt-4">
          <Card title="Report Status" className="dark:bg-theme-paper dark:border-theme-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge 
                  variant={
                    report?.status === 'approved' ? 'success' :
                    report?.status === 'submitted' ? 'success' :
                    report?.status === 'archived' ? 'info' :
                    report?.status === 'in_review' ? 'warning' :
                    'default'
                  }
                  size="md"
                >
                  {report?.status ? report.status.charAt(0).toUpperCase() + report.status.slice(1).replace('_', ' ') : 'Draft'}
                </Badge>
                {report?.status && (report.status === 'draft' || report.status === 'in_review') && (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Document must be approved, archived, or submitted before export
                  </span>
                )}
              </div>
              
              {/* Resume Job Button - Show if job appears stuck */}
              {(() => {
                // Use a separate component for this to manage state properly
                return <ResumeJobButton reportId={reportId} report={report} queryClient={queryClient} />;
              })()}
            </div>
          </Card>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {report.stats?.answered || report.answered_questions || 0}/{report.stats?.total_questions || report.total_questions || 0}
            </div>
            <div className="text-sm text-gray-500">Questions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {report.stats?.avg_confidence 
                ? `${(report.stats.avg_confidence * 100).toFixed(0)}%` 
                : report.avg_confidence 
                ? `${(report.avg_confidence * 100).toFixed(0)}%` 
                : '-'}
            </div>
            <div className="text-sm text-gray-500">Avg Confidence</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {report.stats?.total_citations || report.total_citations || 0}
            </div>
            <div className="text-sm text-gray-500">Citations</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {report.stats?.evidence_gaps || report.evidence_gaps_count || report.evidence_gaps || 0}
            </div>
            <div className="text-sm text-gray-500">Evidence Gaps</div>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-4 overflow-x-auto">
          {(() => {
            // Word Count tab is now available for ALL reports
            // This is a general feature, not just for RFP proposals
            const baseTabs = [
              { id: 'content', label: 'Report Content', icon: FileText },
              { id: 'citations', label: 'Citations & Evidence', icon: BookOpen },
              ...(enableCustomSections ? [{ id: 'customsections', label: 'Custom Sections', icon: Plus }] : []),
              { id: 'staffassignments', label: 'Staff Assignments', icon: Users },
              { id: 'versions', label: 'Version History', icon: History },
              { id: 'wordcount', label: 'Word Count', icon: Scissors },
              ...(enableQualityAssurance ? [{ id: 'qa', label: 'Quality Assurance', icon: ClipboardCheck }] : []),
              { id: 'actions', label: 'Actions', icon: GitBranch, badge: report?.evidence_gaps || 0 },
              { id: 'collaboration', label: 'Comments & Review', icon: MessageSquare },
              ...(enableReviewWorkflow ? [{ id: 'review', label: 'Review Workflow', icon: Shield }] : []),
              { id: 'sharing', label: 'Sharing', icon: Share2 },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp },
            ];
            
            return baseTabs;
          })().map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 font-medium'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-yellow-500 text-white rounded-full text-xs font-bold">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'content' && (
          <Card>
            <div className="space-y-4">
              {/* View Mode Toggle and Edit Controls */}
              <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                <div className="flex items-center gap-2">
                  {/* Version Selector - Show all available versions */}
                  {(() => {
                    const currentVersion = report?.meta?.current_version || 2;
                    const versions: Array<{ value: string; label: string }> = [];
                    
                    // Always include v1 and v2
                    versions.push({ value: 'v2', label: 'V2 - Executive Summary' });
                    versions.push({ value: 'v1', label: 'V1 - Research Draft' });
                    
                    // Check meta for all versions (v3, v4, etc.) by looking for vX_blob_path keys
                    if (report?.meta && typeof report.meta === 'object') {
                      const meta = report.meta as Record<string, any>;
                      const foundVersions = new Set<number>();
                      
                      // Find all version numbers from blob_path keys
                      Object.keys(meta).forEach(key => {
                        if (key.match(/^v(\d+)_blob_path$/)) {
                          const versionNum = parseInt(key.match(/^v(\d+)_blob_path$/)?.[1] || '0');
                          if (versionNum >= 3) {
                            foundVersions.add(versionNum);
                          }
                        }
                      });
                      
                      // Add found versions in descending order (newest first)
                      Array.from(foundVersions).sort((a, b) => b - a).forEach(v => {
                        const changeSummary = meta[`v${v}_change_summary`] || 'Manual edit';
                        const createdBy = meta[`v${v}_created_by`] || 'Unknown';
                        versions.unshift({ 
                          value: `v${v}`, 
                          label: `V${v} - ${changeSummary.substring(0, 30)}${changeSummary.length > 30 ? '...' : ''}` 
                        });
                      });
                    }
                    
                    // Also add versions up to current_version if not already found
                    for (let v = 3; v <= currentVersion; v++) {
                      if (!versions.find(vv => vv.value === `v${v}`)) {
                        versions.unshift({ value: `v${v}`, label: `V${v} - Latest` });
                      }
                    }
                    
                    // Sort: highest version first, then v2, then v1
                    versions.sort((a, b) => {
                      const aNum = parseInt(a.value.substring(1)) || 0;
                      const bNum = parseInt(b.value.substring(1)) || 0;
                      return bNum - aNum;
                    });
                    
                    return (
                      <select
                        value={selectedVersion || `v${currentVersion}`}
                        onChange={(e) => setSelectedVersion(e.target.value as any)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium bg-white hover:bg-gray-50 transition-colors"
                      >
                        {versions.map(v => (
                          <option key={v.value} value={v.value}>{v.label}</option>
                        ))}
                      </select>
                    );
                  })()}
                  
                  <div className="h-6 w-px bg-gray-300 mx-1" />
                  
                  <button
                    onClick={() => setViewMode('rendered')}
                    className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${
                      viewMode === 'rendered'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Eye className="h-4 w-4" />
                    Rendered
                  </button>
                  <button
                    onClick={() => setViewMode('markdown')}
                    className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${
                      viewMode === 'markdown'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Code className="h-4 w-4" />
                    Markdown
                  </button>
                </div>

                {!isEditing ? (
                  <Button size="sm" onClick={() => setIsEditing(true)}>
                    <Edit className="h-4 w-4" />
                    Edit Content
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsEditing(false);
                        setEditedContent(reportContent);
                      }}
                    >
                      <XIcon className="h-4 w-4" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveContent}>
                      <Save className="h-4 w-4" />
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>

              {/* Content Display/Edit Area */}
              {isEditing ? (
                <RichTextEditor
                  value={editedContent}
                  onChange={(markdown) => setEditedContent(markdown)}
                  placeholder="Enter report content in Markdown..."
                  minHeight="600px"
                />
              ) : (
                <div className="min-h-[600px]">
                  {(() => {
                    // Determine which content to show based on selected version
                    const contentToShow = selectedVersion === 'v1' ? reportV1Content : reportContent;
                    
                    if (!contentToShow) {
                      return (
                        <div className="text-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                          <p className="text-gray-600">Loading {selectedVersion || 'v2'} content...</p>
                        </div>
                      );
                    }
                    
                    return viewMode === 'rendered' ? (
                      <div 
                        className="prose prose-slate max-w-none"
                        dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(contentToShow) }}
                      />
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
                          {contentToShow}
                        </pre>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </Card>
        )}

        {activeTab === 'citations' && (
          <Card title="Citations & Evidence Trail">
            <div className="space-y-4">
              {/* Extract all citations from v1.md content */}
              {(() => {
                let citations: any[] = [];
                
                // Extract citations from v1 markdown content with full context
                if (reportV1Content) {
                  // Strategy 1: Try to find Citations section
                  let citationsSectionMatch = reportV1Content.match(/## Citations\s*([\s\S]*?)(?=\n##|$)/);
                  
                  if (citationsSectionMatch) {
                    const citationsSection = citationsSectionMatch[1];
                    
                    // Debug logging (only in development)
                    if (process.env.NODE_ENV === 'development') {
                      console.log('[ReportViewer] Citations section found, length:', citationsSection.length);
                      console.log('[ReportViewer] First 500 chars:', citationsSection.substring(0, 500));
                    }
                    
                    // Split by newlines - keep all lines including empty ones for context
                    const allLines = citationsSection.split('\n');
                    const citationsMap = new Map<number, any>();
                    
                    // Parse citations section line by line with proper context
                    for (let i = 0; i < allLines.length; i++) {
                      const line = allLines[i];
                      const cleanLine = line.replace(/<[^>]*>/g, '').trim();
                      
                      // Skip empty lines, HTML anchors, and markdown links
                      if (!cleanLine || cleanLine.length < 3) continue;
                      
                      // Skip markdown citation links like [1](#citation-1) or (#citation-1)
                      if (cleanLine.match(/^\(?#citation-\d+\)?/i) || cleanLine.match(/\[.*\]\(#citation-\d+\)/)) {
                        continue;
                      }
                      
                      // Match citation header: ### [N] filename.ext
                      // This is the primary format from citation_manager.py
                      const headerMatch = cleanLine.match(/^###\s+\[(\d+)\]\s+(.+)$/);
                      
                      if (headerMatch) {
                        const citationNum = parseInt(headerMatch[1]);
                        let source = headerMatch[2].trim();
                        
                        // Remove any trailing markdown or special characters
                        source = source.replace(/\s*[-\u2013\u2014].*$/, '').trim();
                        
                        // Skip if source looks like a markdown link
                        if (source.match(/^\[.*\]\(/)) continue;
                        
                        // Initialize citation object
                        const citation: any = {
                          index: citationNum,
                          source: source, // Full filename
                          text: '',
                          quote: '',
                          page: null,
                          confidence: undefined,
                          section: 'Research Draft',
                          evidence_id: null,
                          artifact_id: null,
                        };
                        
                        // Look ahead for metadata lines (Page, Confidence, Preview)
                        // Increase lookahead to 10 lines to catch multi-line previews
                        for (let j = i + 1; j < Math.min(i + 11, allLines.length); j++) {
                          const nextLine = allLines[j].replace(/<[^>]*>/g, '').trim();
                          
                          // Stop if we hit another citation header
                          if (nextLine.match(/^###\s+\[\d+\]/)) break;
                          
                          // Skip empty lines but continue looking
                          if (!nextLine) continue;
                          
                          // Extract Page
                          const pageMatch = nextLine.match(/^[-*]\s*\*\*Page:\*\*\s*(\d+)/i) || 
                                          nextLine.match(/^[-*]\s*Page[:\s]+(\d+)/i);
                          if (pageMatch && !citation.page) {
                            citation.page = parseInt(pageMatch[1]);
                          }
                          
                          // Extract Confidence
                          const confMatch = nextLine.match(/^[-*]\s*\*\*Confidence:\*\*\s*([\d.]+)%?/i) ||
                                          nextLine.match(/^[-*]\s*Confidence[:\s]+([\d.]+)%?/i);
                          if (confMatch && citation.confidence === undefined) {
                            const confValue = parseFloat(confMatch[1]);
                            citation.confidence = confValue > 1 ? confValue / 100 : confValue;
                          }
                          
                          // Extract Preview/Content - this is the actual chunk content
                          // Handle multiple formats: - **Preview:** "content..." or - Preview: "content..."
                          // The content may have escaped quotes or be on multiple lines
                          if (!citation.quote) {
                            // Primary format: - **Preview:** "content..."
                            let previewMatch = nextLine.match(/^[-*]\s*\*\*Preview:\*\*\s*"((?:[^"\\]|\\.)+)"/i);
                            
                            if (!previewMatch) {
                              // Alternative format: - **Preview:** "content... (without closing quote on same line)
                              previewMatch = nextLine.match(/^[-*]\s*\*\*Preview:\*\*\s*"((?:[^"\\]|\\.)+)/i);
                            }
                            
                            if (!previewMatch) {
                              // Fallback: - Preview: "content..."
                              previewMatch = nextLine.match(/^[-*]\s*Preview[:\s]+"((?:[^"\\]|\\.)+)"/i);
                            }
                            
                            if (!previewMatch) {
                              // Fallback: - Preview: "content... (without closing quote)
                              previewMatch = nextLine.match(/^[-*]\s*Preview[:\s]+"((?:[^"\\]|\\.)+)/i);
                            }
                            
                            if (previewMatch) {
                              let previewText = previewMatch[1].trim();
                              // Handle escaped quotes and special characters
                              previewText = previewText.replace(/\\"/g, '"')
                                                       .replace(/\\n/g, '\n')
                                                       .replace(/\\t/g, '\t')
                                                       .replace(/\\r/g, '\r');
                              
                              // Check if preview continues on next line (multi-line preview)
                              if (j + 1 < allLines.length && !allLines[j + 1].trim().match(/^[-*]\s*\*\*/) && 
                                  !allLines[j + 1].trim().match(/^###\s+\[\d+\]/)) {
                                const nextLineContent = allLines[j + 1].replace(/<[^>]*>/g, '').trim();
                                // If next line doesn't look like a new metadata field, append it
                                if (nextLineContent && !nextLineContent.match(/^[-*]\s*(Page|Confidence|Preview)/i)) {
                                  previewText += ' ' + nextLineContent.replace(/^"|"$|\.\.\.$/g, '');
                                }
                              }
                              
                              // Remove trailing ellipsis if present
                              previewText = previewText.replace(/\.\.\.$/, '').trim();
                              
                              if (previewText.length > 0) {
                                citation.quote = previewText;
                                citation.text = previewText;
                                
                                // Debug logging (only in development)
                                if (process.env.NODE_ENV === 'development') {
                                  console.log(`[ReportViewer] Extracted preview for citation ${citationNum}:`, {
                                    length: previewText.length,
                                    preview: previewText.substring(0, 100) + '...'
                                  });
                                }
                              }
                            }
                            
                            // Also check for content without quotes (fallback)
                            if (!citation.quote) {
                              const contentMatch = nextLine.match(/^[-*]\s*\*\*Preview:\*\*\s*(.+?)(?:\.\.\.)?$/i) ||
                                                 nextLine.match(/^[-*]\s*Preview[:\s]+(.+?)(?:\.\.\.)?$/i);
                              if (contentMatch && contentMatch[1].trim().length > 10) {
                                citation.quote = contentMatch[1].trim().replace(/\.\.\.$/, '');
                                citation.text = citation.quote;
                                
                                if (process.env.NODE_ENV === 'development') {
                                  console.log(`[ReportViewer] Extracted preview (no quotes) for citation ${citationNum}`);
                                }
                              }
                            }
                          }
                        }
                        
                        // Only add if we have a valid source filename
                        if (source && source.length > 0 && !source.match(/^[#\[\]()]/)) {
                          // Log if we're missing content (for debugging)
                          if (process.env.NODE_ENV === 'development' && !citation.quote && !citation.text) {
                            console.warn(`[ReportViewer] Citation ${citationNum} has no preview content:`, {
                              source,
                              page: citation.page,
                              confidence: citation.confidence
                            });
                          }
                          citationsMap.set(citationNum, citation);
                        }
                      }
                    }
                    
                    // Convert map to sorted array
                    citations = Array.from(citationsMap.values())
                      .sort((a, b) => a.index - b.index);
                  }
                  
                  // Strategy 2: If no citations section or empty, try to extract from report sections
                  // This is a fallback - should rarely be needed if Citations section exists
                  if (citations.length === 0) {
                    // Try to find citations embedded in report sections
                    // Look for patterns like "Source: filename.pdf" or references to documents
                    const citationPatterns = [
                      /Source[:\s]+([^\n,]+\.(pdf|txt|xlsx?|docx?|csv|pptx?))/gi,
                      /Document[:\s]+([^\n,]+\.(pdf|txt|xlsx?|docx?|csv|pptx?))/gi,
                      /\[(\d+)\]\s+([^\n]+\.(pdf|txt|xlsx?|docx?|csv|pptx?))/gi,
                    ];
                    
                    const foundCitations = new Map<number, any>();
                    
                    for (const pattern of citationPatterns) {
                      let match;
                      while ((match = pattern.exec(reportV1Content)) !== null) {
                        const num = match[1] ? parseInt(match[1]) : foundCitations.size + 1;
                        const filename = match[2] || match[1] || `Document ${num}`;
                        
                        if (!foundCitations.has(num) && filename && filename.length > 0) {
                          foundCitations.set(num, {
                            index: num,
                            source: filename.trim(),
                            text: '',
                            quote: '',
                            page: null,
                            confidence: undefined,
                            section: 'Research Draft',
                            evidence_id: null,
                            artifact_id: null,
                          });
                        }
                      }
                    }
                    
                    citations = Array.from(foundCitations.values())
                      .sort((a, b) => a.index - b.index);
                  }
                  
                  // If no citations section found, extract inline citations with context
                  if (citations.length === 0) {
                    const citationNumbers = new Set<number>();
                    const inlineCitations = reportV1Content.matchAll(/\[(\d+)\]/g);
                    
                    for (const match of inlineCitations) {
                      citationNumbers.add(parseInt(match[1]));
                    }
                    
                    // Extract citation context from document
                    const lines = reportV1Content.split('\n');
                    const citationMap = new Map<number, { source: string; context: string }>();
                    
                    for (let i = 0; i < lines.length; i++) {
                      const match = lines[i].match(/\[(\d+)\]\s*(.+)/);
                      if (match && citationNumbers.has(parseInt(match[1]))) {
                        const citNum = parseInt(match[1]);
                        const source = match[2].trim();
                        
                        // Get surrounding context (previous line for quote)
                        const context = i > 0 ? lines[i - 1].substring(0, 150) : '';
                        
                        citationMap.set(citNum, { source, context });
                      }
                    }
                    
                    citations = Array.from(citationNumbers).sort((a, b) => a - b).map(num => {
                      const data = citationMap.get(num);
                      return {
                        index: num,
                        source: data?.source || `Citation ${num}`,
                        text: data?.context || '',
                        quote: data?.context || '',
                        section: 'Research Draft',
                        evidence_id: null,
                        artifact_id: null,
                      };
                    });
                  }
                }
                
                // Build comprehensive citation map from sections - GROUP BY DOCUMENT (artifact_id)
                // This ensures one citation per document, with all chunks collected
                const citationToSectionsMap = new Map<string, Set<string>>(); // citation key -> set of section names
                const allCitationsMap = new Map<string, any>(); // citation key -> citation object
                
                if (report?.sections && Array.isArray(report.sections)) {
                  report.sections.forEach((section: any) => {
                    if (section.citations && Array.isArray(section.citations)) {
                      section.citations.forEach((citation: any) => {
                        // Group by artifact_id (document) - this is the key change
                        // If no artifact_id, group by source filename
                        const citationKey = citation.artifact_id 
                          ? `artifact_${citation.artifact_id}`
                          : citation.source || citation.artifact_filename || 'unknown';
                        
                        // Get document name - prioritize artifact_filename, then source
                        const documentName = citation.artifact_filename || citation.source || 'Unknown Document';
                        
                        if (!allCitationsMap.has(citationKey)) {
                          // First time seeing this document - create citation entry
                          allCitationsMap.set(citationKey, {
                            index: allCitationsMap.size + 1,
                            source: documentName, // Document name
                            artifact_id: citation.artifact_id,
                            // Collect all chunks/quotes for this document
                            chunks: citation.chunk_id ? [{
                              chunk_id: citation.chunk_id,
                              text: citation.text || citation.content || '',
                              quote: citation.text || citation.content || '',
                              page: citation.page || citation.page_number,
                              confidence: citation.confidence,
                              evidence_id: citation.evidence_id,
                            }] : [],
                            // For display, use first chunk's content as primary quote
                            text: citation.text || citation.content || '',
                            quote: citation.text || citation.content || '',
                            // Collect all pages
                            pages: citation.page || citation.page_number ? [citation.page || citation.page_number] : [],
                            page: citation.page || citation.page_number, // Primary page (first one)
                            // Sections using this citation
                            sections: [section.section_name],
                            section: section.section_name,
                            // Confidence - average or use first one
                            confidence: citation.confidence,
                            confidences: citation.confidence ? [citation.confidence] : [],
                            // Evidence and chunk IDs
                            evidence_id: citation.evidence_id,
                            chunk_id: citation.chunk_id, // Primary chunk_id (first one)
                            chunk_ids: citation.chunk_id ? [citation.chunk_id] : [],
                            // Section IDs
                            section_id: section.id,
                            section_ids: [section.id],
                          });
                          citationToSectionsMap.set(citationKey, new Set([section.section_name]));
                        } else {
                          // Document already exists - add this chunk to it
                          const existing = allCitationsMap.get(citationKey);
                          
                          // Add section if not already present
                          if (!existing.sections.includes(section.section_name)) {
                            existing.sections.push(section.section_name);
                          }
                          if (!existing.section_ids.includes(section.id)) {
                            existing.section_ids.push(section.id);
                          }
                          citationToSectionsMap.get(citationKey)?.add(section.section_name);
                          
                          // Add chunk if it's a new chunk
                          if (citation.chunk_id && !existing.chunk_ids.includes(citation.chunk_id)) {
                            existing.chunk_ids.push(citation.chunk_id);
                            existing.chunks.push({
                              chunk_id: citation.chunk_id,
                              text: citation.text || citation.content || '',
                              quote: citation.text || citation.content || '',
                              page: citation.page || citation.page_number,
                              confidence: citation.confidence,
                              evidence_id: citation.evidence_id,
                            });
                          }
                          
                          // Add page if new
                          const page = citation.page || citation.page_number;
                          if (page && !existing.pages.includes(page)) {
                            existing.pages.push(page);
                          }
                          
                          // Add confidence if available
                          if (citation.confidence && !existing.confidences.includes(citation.confidence)) {
                            existing.confidences.push(citation.confidence);
                            // Update average confidence
                            const avgConf = existing.confidences.reduce((a: number, b: number) => a + b, 0) / existing.confidences.length;
                            existing.confidence = avgConf;
                          }
                          
                          // Update primary quote if this one is longer/more informative
                          const newText = citation.text || citation.content || '';
                          if (newText && newText.length > (existing.quote?.length || 0)) {
                            existing.quote = newText;
                            existing.text = newText;
                          }
                        }
                      });
                    }
                  });
                  
                  // If we have citations from sections, use them (they have more metadata)
                  if (allCitationsMap.size > 0) {
                    citations = Array.from(allCitationsMap.values());
                  }
                }

                if (citations.length === 0) {
                  return (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-600 font-medium mb-2">No citations available</p>
                      <p className="text-sm text-gray-500">
                        {!reportV1Content ? 'Loading v1 report content for citations...' : 'This report does not contain any citations'}
                      </p>
                      {!reportV1Content && (
                        <button 
                          onClick={() => {
                            downloadReportV1(reportId)
                              .then(content => setReportV1Content(content))
                              .catch(err => console.error('Failed to load v1:', err));
                          }}
                          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Load Citations from V1
                        </button>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm text-blue-900 dark:text-blue-300 font-medium">
                            <strong>{citations.length}</strong> citation{citations.length !== 1 ? 's' : ''} found in this report
                          </p>
                          {removedArtifactIds.size > 0 ? (
                            <p className="text-xs text-orange-700 dark:text-orange-400 mt-1 font-medium">
                              ⚠️ {removedArtifactIds.size} source{removedArtifactIds.size !== 1 ? 's' : ''} marked for exclusion. 
                              These will be excluded when you regenerate sections - they won't be used again.
                            </p>
                          ) : (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              💡 Click the trash icon on any citation to remove it, then use "Regenerate Sections" to update the report. 
                              Removed citations will be excluded from future regenerations.
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {citations.some(c => c.confidence !== undefined && c.confidence !== null) && (
                            <div className="text-xs text-blue-700 dark:text-blue-400 whitespace-nowrap">
                              {citations.filter(c => c.confidence && c.confidence >= 0.7).length} high confidence
                            </div>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              // If report has sections, open modal with section selector
                              if (report?.sections && report.sections.length > 0) {
                                setShowAddCitationModal(true);
                              } else {
                                alert('No sections available. Please ensure the report has sections before adding citations.');
                              }
                            }}
                            className="flex items-center gap-2"
                            title="Add new citations to a report section"
                          >
                            <Plus className="h-4 w-4" />
                            Add Citation
                          </Button>
                          {removedArtifactIds.size > 0 && (
                            <Button
                              size="sm"
                              onClick={() => handleRegenerateSections()}
                              disabled={isRegenerating}
                              className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white"
                            >
                              <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                              {isRegenerating ? 'Regenerating...' : `Regenerate ${report?.sections?.length || 0} Sections`}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Bulk Operations - Select multiple citations */}
                    {citations.length > 1 && (
                      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {!isBulkMode ? (
                              <>
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  Select multiple citations to remove in bulk
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setIsBulkMode(true)}
                                  className="text-xs"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Select Multiple
                                </Button>
                              </>
                            ) : (
                              <>
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  {selectedCitationIds.size} citation{selectedCitationIds.size !== 1 ? 's' : ''} selected
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedCitationIds(new Set());
                                    setIsBulkMode(false);
                                  }}
                                  className="text-xs"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={async () => {
                                    if (selectedCitationIds.size === 0) {
                                      alert('Please select at least one citation to remove');
                                      return;
                                    }
                                    
                                    const selectedCitations = citations.filter(c => {
                                      const citationKey = c.artifact_id || c.source || `citation-${c.index}`;
                                      return selectedCitationIds.has(citationKey);
                                    });
                                    
                                    const affectedSections = new Set<string>();
                                    const affectedSectionIds = new Set<string>();
                                    selectedCitations.forEach(c => {
                                      (c.sections || []).forEach((s: string) => affectedSections.add(s));
                                      (c.section_ids || []).forEach((id: string) => affectedSectionIds.add(id));
                                    });
                                    
                                    const confirmMessage = `Remove ${selectedCitationIds.size} citation(s) from the report?\n\n` +
                                      `Affected Sections: ${Array.from(affectedSections).join(', ')}\n` +
                                      `This will remove the citations and you can regenerate ${affectedSectionIds.size} section(s) without these sources.`;
                                    
                                    if (!confirm(confirmMessage)) {
                                      return;
                                    }
                                    
                                    try {
                                      // Remove each citation
                                      for (const citation of selectedCitations) {
                                        if (citation.artifact_id) {
                                          await removeCitation(reportId, { 
                                            artifact_id: citation.artifact_id
                                          });
                                          setRemovedArtifactIds(prev => new Set([...prev, citation.artifact_id!]));
                                        }
                                      }
                                      
                                      // Clear selection
                                      setSelectedCitationIds(new Set());
                                      setIsBulkMode(false);
                                      
                                      // Refresh report content
                                      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
                                      downloadReportV1(reportId)
                                        .then(content => setReportV1Content(content))
                                        .catch(err => console.error('Failed to reload v1:', err));
                                      downloadReportV2(reportId)
                                        .then(content => {
                                          setReportContent(content);
                                          setEditedContent(content);
                                        })
                                        .catch(err => console.error('Failed to reload v2:', err));
                                      
                                      alert(`Successfully removed ${selectedCitationIds.size} citation(s). ${affectedSectionIds.size} section(s) affected. Use "Regenerate Sections" to update the report content.`);
                                    } catch (error: any) {
                                      console.error('Failed to remove citations:', error);
                                      alert(`Failed to remove citations: ${error.message || 'Unknown error'}`);
                                    }
                                  }}
                                  disabled={selectedCitationIds.size === 0}
                                  className="text-xs bg-red-600 hover:bg-red-700 text-white"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Remove {selectedCitationIds.size} Selected
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {citations.map((citation) => {
                      // Get affected section IDs for this citation
                      const affectedSectionIds = citation.section_ids || 
                        (report?.sections
                          ?.filter((s: any) => (citation.sections || [citation.section]).includes(s.section_name))
                          .map((s: any) => s.id) || []);
                      
                      const citationKey = citation.artifact_id || citation.source || `citation-${citation.index}`;
                      const isSelected = selectedCitationIds.has(citationKey);
                      
                      return (
                      <div 
                        key={citation.index} 
                        className={`bg-white dark:bg-theme-paper border-2 rounded-lg p-4 transition-all shadow-sm hover:shadow-md ${
                          isBulkMode 
                            ? isSelected
                              ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                        } ${isBulkMode ? 'cursor-pointer' : ''}`}
                        onClick={isBulkMode ? () => {
                          setSelectedCitationIds(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(citationKey)) {
                              newSet.delete(citationKey);
                            } else {
                              newSet.add(citationKey);
                            }
                            return newSet;
                          });
                        } : undefined}
                      >
                        <div className="flex items-start gap-3">
                          {/* Selection Checkbox (only in bulk mode) */}
                          {isBulkMode && (
                            <div className="flex-shrink-0 mt-1" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setSelectedCitationIds(prev => {
                                    const newSet = new Set(prev);
                                    if (newSet.has(citationKey)) {
                                      newSet.delete(citationKey);
                                    } else {
                                      newSet.add(citationKey);
                                    }
                                    return newSet;
                                  });
                                }}
                                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                              />
                            </div>
                          )}
                          <div className="bg-blue-600 dark:bg-blue-500 text-white rounded-full w-9 h-9 flex items-center justify-center font-bold flex-shrink-0 text-sm shadow-sm">
                            {citation.index}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2 gap-2">
                              <button
                                onClick={() => handleDownloadByFilename(citation.source, citation.artifact_id)}
                                className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer group flex-1 min-w-0"
                                title={`Click to download: ${citation.source}`}
                              >
                                <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 group-hover:text-blue-700 dark:group-hover:text-blue-300" />
                                <span className="group-hover:underline truncate" title={citation.source}>
                                  {citation.source || 'Unknown Source'}
                                </span>
                                <Download className="h-3 w-3 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                              </button>
                              <div className="flex gap-1 flex-shrink-0 items-center">
                                {citation.artifact_id ? (
                                  <>
                                    {removedArtifactIds.has(citation.artifact_id) ? (
                                      <>
                                        <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-medium whitespace-nowrap">
                                          Removed
                                        </span>
                                        {affectedSectionIds.length > 0 && !isBulkMode && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleRegenerateSections(affectedSectionIds)}
                                            disabled={isRegenerating}
                                            className="text-xs bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/30"
                                            title={`Regenerate ${affectedSectionIds.length} affected section(s) without this source`}
                                          >
                                            <RefreshCw className={`h-3 w-3 ${isRegenerating ? 'animate-spin' : ''}`} />
                                            Regenerate
                                          </Button>
                                        )}
                                      </>
                                    ) : (
                                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs font-medium whitespace-nowrap">
                                        Available
                                      </span>
                                    )}
                                    {!isBulkMode && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveCitation(citation)}
                                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                                        title={`Remove this citation from ${citation.sections?.length || 1} section(s) and regenerate without it`}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded text-xs font-medium whitespace-nowrap" title="This citation does not have an associated artifact ID">
                                    No artifact ID
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Quote/Excerpt - Show chunks from this document */}
                            {citation.chunks && citation.chunks.length > 0 ? (
                              <div className="space-y-2 mb-2">
                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                  {citation.chunks.length} chunk{citation.chunks.length !== 1 ? 's' : ''} from this document:
                                </div>
                                {citation.chunks.slice(0, 3).map((chunk: any, idx: number) => (
                                  <div key={idx} className="bg-gray-50 dark:bg-gray-800 border-l-4 border-blue-400 dark:border-blue-500 pl-3 py-2">
                                    <div className="flex items-start gap-2">
                                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide flex-shrink-0 mt-0.5">
                                        Chunk {idx + 1}:
                                      </span>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed flex-1">
                                        "{chunk.quote || chunk.text || 'No content'}"
                                      </p>
                                    </div>
                                    {chunk.page && (
                                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-12">
                                        Page {chunk.page}
                                      </p>
                                    )}
                                  </div>
                                ))}
                                {citation.chunks.length > 3 && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                    + {citation.chunks.length - 3} more chunk{citation.chunks.length - 3 !== 1 ? 's' : ''}
                                  </p>
                                )}
                              </div>
                            ) : citation.quote && citation.quote.trim() && citation.quote !== citation.source && !citation.quote.match(/^[#\[\]()]/) ? (
                              <div className="bg-gray-50 dark:bg-gray-800 border-l-4 border-blue-400 dark:border-blue-500 pl-3 py-2 mb-2">
                                <div className="flex items-start gap-2">
                                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide flex-shrink-0 mt-0.5">
                                    Excerpt:
                                  </span>
                                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed flex-1">
                                    "{citation.quote}"
                                  </p>
                                </div>
                              </div>
                            ) : citation.text && citation.text.trim() && citation.text !== citation.source && !citation.text.match(/^[#\[\]()]/) ? (
                              <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 mb-2 border border-gray-200 dark:border-gray-700">
                                <div className="flex items-start gap-2">
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex-shrink-0 mt-0.5">
                                    Content:
                                  </span>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">
                                    {citation.text}
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-300 dark:border-blue-600 pl-3 py-2 mb-2">
                                <p className="text-sm text-blue-700 dark:text-blue-300">
                                  <span className="font-medium">Document:</span> {citation.source}
                                  {citation.pages && citation.pages.length > 0 && (
                                    <span className="ml-2 text-blue-600 dark:text-blue-400">
                                      (Pages {citation.pages.sort((a: number, b: number) => a - b).join(', ')})
                                    </span>
                                  )}
                                </p>
                              </div>
                            )}
                            
                            {/* Metadata Row - Confidence, Page, Artifact ID, Chunk ID */}
                            <div className="flex flex-wrap items-center gap-2 text-xs mt-2">
                              {/* Confidence - Prominently displayed */}
                              {citation.confidence !== undefined && citation.confidence !== null && !isNaN(citation.confidence) && (
                                <span className={`px-3 py-1.5 rounded-lg font-semibold border-2 ${
                                  citation.confidence >= 0.7 
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700' 
                                    : citation.confidence >= 0.5
                                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700'
                                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700'
                                }`}>
                                  {citation.confidence >= 0.7 ? '✓' : citation.confidence >= 0.5 ? '⚠' : '✗'} {Math.round(citation.confidence * 100)}% Confidence
                                </span>
                              )}
                              
                              {/* Pages - Show all pages from this document */}
                              {citation.pages && citation.pages.length > 0 ? (
                                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-medium border border-gray-200 dark:border-gray-600">
                                  📄 Pages {citation.pages.sort((a: number, b: number) => a - b).join(', ')}
                                </span>
                              ) : citation.page ? (
                                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-medium border border-gray-200 dark:border-gray-600">
                                  📄 Page {citation.page}
                                </span>
                              ) : null}
                              
                              {/* Chunk Count */}
                              {citation.chunk_ids && citation.chunk_ids.length > 1 && (
                                <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded font-medium border border-indigo-200 dark:border-indigo-800 text-xs">
                                  {citation.chunk_ids.length} chunks
                                </span>
                              )}
                              
                              {/* Artifact ID - Copy to clipboard */}
                              {citation.artifact_id && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(citation.artifact_id!);
                                      alert('Artifact ID copied to clipboard!');
                                    } catch (err) {
                                      console.error('Failed to copy:', err);
                                    }
                                  }}
                                  className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors flex items-center gap-1 border border-purple-200 dark:border-purple-800 font-mono text-xs"
                                  title="Click to copy Artifact ID"
                                >
                                  <FileText className="h-3 w-3" />
                                  Artifact: {citation.artifact_id.substring(0, 8)}...
                                </button>
                              )}
                              
                              {/* Chunk IDs - Show all if multiple, or single if one */}
                              {citation.chunk_ids && citation.chunk_ids.length > 0 && (
                                <button
                                  onClick={async () => {
                                    try {
                                      const chunkIdsText = citation.chunk_ids.join(', ');
                                      await navigator.clipboard.writeText(chunkIdsText);
                                      alert(`Copied ${citation.chunk_ids.length} chunk ID(s) to clipboard!`);
                                    } catch (err) {
                                      console.error('Failed to copy:', err);
                                    }
                                  }}
                                  className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors flex items-center gap-1 border border-indigo-200 dark:border-indigo-800 font-mono text-xs"
                                  title={`Click to copy ${citation.chunk_ids.length} chunk ID(s)`}
                                >
                                  <Code className="h-3 w-3" />
                                  {citation.chunk_ids.length === 1 
                                    ? `Chunk: ${citation.chunk_ids[0].substring(0, 8)}...`
                                    : `${citation.chunk_ids.length} chunks`
                                  }
                                </button>
                              )}
                              
                              {/* Section(s) - Show all sections using this citation */}
                              {citation.sections && Array.isArray(citation.sections) ? (
                                <div className="flex flex-wrap gap-1">
                                  {citation.sections.map((sectionName: string, idx: number) => (
                                    <span 
                                      key={idx}
                                      className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium border border-blue-200 dark:border-blue-800"
                                      title={`Used in section: ${sectionName}`}
                                    >
                                      {sectionName}
                                    </span>
                                  ))}
                                  {citation.sections.length > 1 && (
                                    <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded text-xs">
                                      +{citation.sections.length - 1} more
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium border border-blue-200 dark:border-blue-800">
                                  {citation.section || 'Research Draft'}
                                </span>
                              )}
                              
                              {/* Evidence ID - Trace button */}
                              {citation.evidence_id && (
                                <button
                                  onClick={() => handleTraceEvidence(citation.evidence_id!)}
                                  className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors flex items-center gap-1 border border-indigo-200 dark:border-indigo-800"
                                  title="Trace to original evidence and view chunk details"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  Trace Evidence
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )})}
                  </div>
                );
              })()}
            </div>
          </Card>
        )}

        {activeTab === 'versions' && (
          <Card title="Version History">
            <div className="space-y-3">
              {versions.map((version) => (
                <div
                  key={version.version}
                  className={`border rounded-lg p-4 ${
                    version.status === 'current'
                      ? 'border-blue-300 bg-blue-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-gray-100 text-gray-700 rounded-lg px-3 py-1 font-mono text-sm">
                        v{version.version}
                      </div>
                      {version.status === 'current' && (
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => {
                          // Switch to content tab and select this version
                          setActiveTab('content');
                          setSelectedVersion(`v${version.version}`);
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </button>
                      {version.status !== 'current' && (
                        <button 
                          onClick={() => {
                            if (window.confirm(`Restore version ${version.version}? This will replace the current version.`)) {
                              alert('Version restore is not yet implemented. This would call the restore API endpoint.');
                            }
                          }}
                          className="text-green-600 hover:text-green-700 text-sm flex items-center gap-1"
                        >
                          <GitBranch className="h-4 w-4" />
                          Restore
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 text-sm">
                    <p className="text-gray-900 dark:text-gray-100 font-medium">{version.changes}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>By: {version.created_by}</span>
                      <span>{new Date(version.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {activeTab === 'collaboration' && (
          <ReportCollaboration
            reportId={reportId}
            currentStatus={report.status}
            onStatusChange={(newStatus) => {
              queryClient.invalidateQueries({ queryKey: ['report', reportId] });
            }}
          />
        )}

        {activeTab === 'sharing' && (
          <ReportSharing reportId={reportId} />
        )}

        {activeTab === 'analytics' && (
          <ReportAnalytics reportId={reportId} />
        )}

        {activeTab === 'actions' && (
          <ReportActions 
            reportId={reportId}
            evidenceGaps={report?.evidence_gaps}
            totalSections={report?.total_questions}
            onRegenerateComplete={() => {
              queryClient.invalidateQueries({ queryKey: ['report', reportId] });
              setActiveTab('content');
            }}
          />
        )}

        {activeTab === 'wordcount' && (
          <WordCountTabContent reportId={reportId} report={report} enableOptimizer={enableWordCountOptimizer} />
        )}

        {activeTab === 'qa' && (
          <QATabContent reportId={reportId} />
        )}

        {activeTab === 'review' && (
          <ReviewDashboard 
            reportId={reportId}
            onStatusChange={() => {
              queryClient.invalidateQueries({ queryKey: ['report', reportId] });
            }}
          />
        )}

        {activeTab === 'customsections' && (
          <CustomSectionsTab
            reportId={reportId}
            report={report}
            onSectionUpdate={() => {
              // Refresh report data when sections are updated
              queryClient.invalidateQueries({ queryKey: ['report', reportId] });
            }}
          />
        )}

        {activeTab === 'staffassignments' && (
          <StaffAssignmentsTab reportId={reportId} report={report} />
        )}
      </div>

      {/* Add Citation Modal */}
      <AddCitationModal
        isOpen={showAddCitationModal}
        onClose={() => {
          setShowAddCitationModal(false);
          setAddCitationSectionId(undefined);
          setAddCitationSectionName(undefined);
        }}
        reportId={reportId}
        report={report}
        onSuccess={() => {
          // Reload report content after adding citations
          downloadReportV1(reportId)
            .then(content => setReportV1Content(content))
            .catch(err => console.error('Failed to reload v1:', err));
          downloadReportV2(reportId)
            .then(content => {
              setReportContent(content);
              setEditedContent(content);
            })
            .catch(err => console.error('Failed to reload v2:', err));
        }}
      />

      {/* Evidence Trace Modal */}
      {selectedCitation && selectedCitation.evidence_id && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedCitation(null)}>
          <div className="max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <Card>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Evidence Provenance Trail
                  </h3>
                  <button
                    onClick={() => setSelectedCitation(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XIcon className="h-5 w-5" />
                  </button>
                </div>
                
                {selectedCitation.trace ? (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                    {/* Evidence ID */}
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Evidence ID</div>
                      <div className="text-gray-900 dark:text-gray-100 font-mono text-sm bg-white dark:bg-slate-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600">
                        {selectedCitation.evidence_id}
                      </div>
                    </div>
                    
                    {/* Source Document */}
                    {selectedCitation.trace.artifact_filename && (
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">Source Document</div>
                        <div className="text-gray-900 dark:text-gray-100 bg-white dark:bg-slate-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600">
                          {selectedCitation.trace.artifact_filename}
                        </div>
                      </div>
                    )}
                    
                    {/* Content Preview - Full chunk content */}
                    {(selectedCitation.chunk_content || selectedCitation.trace.content) && (
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">Chunk Content</div>
                        <div className="text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 max-h-60 overflow-y-auto text-sm">
                          {selectedCitation.chunk_content || selectedCitation.trace.content}
                        </div>
                      </div>
                    )}
                    
                    {/* Confidence Breakdown */}
                    {selectedCitation.trace.evidence && (
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">Evidence Details</div>
                        <div className="bg-white dark:bg-slate-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 space-y-2 text-sm">
                          {selectedCitation.trace.evidence.evidence_type && (
                            <div>
                              <span className="font-medium text-gray-700 dark:text-gray-300">Type: </span>
                              <span className="text-gray-600 dark:text-gray-400">{selectedCitation.trace.evidence.evidence_type}</span>
                            </div>
                          )}
                          {selectedCitation.trace.evidence.page_number && (
                            <div>
                              <span className="font-medium text-gray-700 dark:text-gray-300">Page: </span>
                              <span className="text-gray-600 dark:text-gray-400">{selectedCitation.trace.evidence.page_number}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Metadata */}
                    <div className="grid grid-cols-2 gap-3">
                      {selectedCitation.trace.page_number && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-1">Page Number</div>
                          <div className="text-gray-900 dark:text-gray-100 bg-white dark:bg-slate-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600">
                            Page {selectedCitation.trace.page_number}
                          </div>
                        </div>
                      )}
                      {selectedCitation.trace.chunk_id && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-1">Chunk ID</div>
                          <div className="flex items-center gap-2">
                            <div className="text-gray-900 font-mono text-xs bg-white dark:bg-slate-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 flex-1 truncate">
                              {selectedCitation.trace.chunk_id}
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(selectedCitation.trace.chunk_id);
                                  alert('Chunk ID copied!');
                                } catch (err) {
                                  console.error('Failed to copy:', err);
                                }
                              }}
                              className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                              title="Copy Chunk ID"
                            >
                              <FileCode className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Chunk Content - Full content from chunk */}
                      {selectedCitation.chunk_content && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-1">Chunk Content</div>
                          <div className="text-gray-700 bg-white dark:bg-slate-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 max-h-60 overflow-y-auto text-sm">
                            {selectedCitation.chunk_content}
                          </div>
                        </div>
                      )}
                      
                      {/* Artifact ID */}
                      {selectedCitation.trace.artifact?.id && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-1">Artifact ID</div>
                          <div className="flex items-center gap-2">
                            <div className="text-gray-900 dark:text-gray-100 font-mono text-xs bg-white dark:bg-slate-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 flex-1 truncate">
                              {selectedCitation.trace.artifact.id}
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(selectedCitation.trace.artifact.id);
                                  alert('Artifact ID copied!');
                                } catch (err) {
                                  console.error('Failed to copy:', err);
                                }
                              }}
                              className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                              title="Copy Artifact ID"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Download Button */}
                    {selectedCitation.trace.artifact_id && (
                      <Button 
                        className="w-full" 
                        onClick={() => handleDownloadArtifact(
                          selectedCitation.trace.artifact_id, 
                          selectedCitation.trace.artifact_filename || 'document.pdf'
                        )}
                      >
                        <Download className="h-4 w-4" />
                        Download Source Document
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                    <p className="text-gray-600">Loading evidence trace...</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}


